import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ContentLayout from "../../component/ContentLayout";
import CustomButton from "../../component/Button";
import { VscLoading } from "react-icons/vsc";
import {
  MdBusiness,
  MdDownload,
  MdVerifiedUser,
  MdOutlinePolicy,
  MdAssessment,
} from "react-icons/md";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { Context } from "../../component/Context/ErrorContext";
import { setCompanyData } from "../../store/reducers/companySlice";
import { COMPANY_CERTIFICATIONS } from "../../util/certificateGenerator";
import {
  captureCertificateElement,
  preloadCertificateAssets,
} from "../../util/certificateExport";
import {
  CERTIFICATE_BUNDLES,
  CompanyBundleType,
  getBundleByType,
  getCompanyCertsForBundle,
} from "../../util/certificateBundles";
import CertificateCard from "../../component/CertificateTemplate";
import { CertificationRecord } from "../../util/certificateTypes";
import { getCompanyDisplayName } from "../../util/companyDisplayName";
import { fetchCompanyDashboard } from "../../util/fetchCompanyDashboard";
import { MdRefresh } from "react-icons/md";
import "../../styles/companyCertifications.css";

const CompanyCertifications = () => {
  const dispatch = useDispatch();
  const { setError } = useContext(Context);
  const companyData = useSelector((state: any) => state?.company?.companyData);

  const displayCompanyName = useMemo(() => {
    const resolved = getCompanyDisplayName(companyData);
    return resolved || "Your Company";
  }, [companyData]);

  const [selectedBundle, setSelectedBundle] =
    useState<CompanyBundleType>("government_contractor");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [showBundleOnly, setShowBundleOnly] = useState(true);
  const captureRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const activeBundle = getBundleByType(selectedBundle);
  const bundleCerts = useMemo(
    () => getCompanyCertsForBundle(selectedBundle),
    [selectedBundle]
  );

  const companyProgramName = useMemo(() => {
    if (selectedBundle === "government_contractor") {
      return "FCA Protection Program";
    }
    if (selectedBundle === "public") {
      return "SEC Investor Mitigation Reporting";
    }
    return "Private Company Safety & Protection Programs";
  }, [selectedBundle]);

  const companyProgramDescription = useMemo(() => {
    if (selectedBundle === "government_contractor") {
      return "Federal contractor program — FCA-aligned employee safety proof and compliance documentation.";
    }
    if (selectedBundle === "public") {
      return "Public company program — SEC investor mitigation reporting and employee safety compliance.";
    }
    return "Private company safety and protection programs — workforce readiness across OnMyWay protection features.";
  }, [selectedBundle]);

  const displayedCerts = useMemo(
    () => (showBundleOnly ? bundleCerts : COMPANY_CERTIFICATIONS),
    [showBundleOnly, bundleCerts]
  );

  const loadCompanyData = useCallback(async () => {
    setLoadingCompany(true);
    try {
      const data = await fetchCompanyDashboard();
      if (data) {
        dispatch(setCompanyData(data));
      }
    } catch (err: any) {
      setError(err.response?.data?.message);
    } finally {
      setLoadingCompany(false);
    }
  }, [dispatch, setError]);

  useEffect(() => {
    loadCompanyData();
    preloadCertificateAssets();
  }, [loadCompanyData]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadCompanyData();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadCompanyData]);

  const getSafeName = (v: string) => v.trim().replace(/\s+/g, "_");

  const canvasToPdfBlob = async (canvas: HTMLCanvasElement): Promise<Blob> => {
    const pngBlob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create PNG"));
      }, "image/png");
    });
    const pngBytes = await pngBlob.arrayBuffer();
    const pdfDoc = await PDFDocument.create();
    const pngImage = await pdfDoc.embedPng(pngBytes);
    const page = pdfDoc.addPage([canvas.width, canvas.height]);
    page.drawImage(pngImage, {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height,
    });
    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const capturePreviewAsCanvas = async (certId: string) => {
    const node = captureRefs.current[certId];
    if (!node) {
      throw new Error("Certificate preview not found. Scroll to the card and try again.");
    }
    node.scrollIntoView({ block: "center", behavior: "auto" });
    await new Promise((r) => setTimeout(r, 200));
    return captureCertificateElement(node);
  };

  const downloadCertPdf = async (certId: string) => {
    const cert = COMPANY_CERTIFICATIONS.find((c) => c.id === certId);
    if (!cert) return;
    setDownloadingId(certId);
    try {
      const canvas = await capturePreviewAsCanvas(certId);
      const pdf = await canvasToPdfBlob(canvas);
      downloadBlob(pdf, `${getSafeName(displayCompanyName)}_${cert.id}.pdf`);
    } catch (e) {
      console.error("Error downloading certificate:", e);
      setError("Could not download certificate. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const renderCompanyCertCard = (cert: CertificationRecord) => (
    <article
      key={cert.id}
      className="company-certs-card company-certs-card--in-bundle"
    >
      <div
        className="company-certs-card__accent"
        style={{ background: cert.themeColor }}
      />
      <div
        className="company-certs-card__preview certificate-capture"
        ref={(el) => {
          captureRefs.current[cert.id] = el;
        }}
      >
        <CertificateCard
          cert={cert}
          recipientName={displayCompanyName}
          showBrandName={false}
          showIcon={false}
          elementId={`company-cert-${cert.id}`}
          className="certificate--modal certificate--square"
        />
      </div>
      <div className="company-certs-card__body">
        <div className="company-certs-card__header">
          <h3 className="company-certs-card__title">{cert.title}</h3>
          <span className="company-certs-card__pill company-certs-card__pill--bundle">
            In bundle
          </span>
        </div>
        <p className="company-certs-card__desc">{cert.description}</p>
        <CustomButton
          title={
            downloadingId === cert.id ? (
              <VscLoading className="animate-spin" />
            ) : (
              "Download PDF"
            )
          }
          onClick={() => downloadCertPdf(cert.id)}
          disabled={bundleLoading || downloadingId === cert.id}
          className="company-certs-card__download !py-2 !text-sm w-full"
        />
      </div>
    </article>
  );

  const downloadBundleZip = async () => {
    setBundleLoading(true);
    try {
      const zip = new JSZip();
      for (const cert of bundleCerts) {
        const canvas = await capturePreviewAsCanvas(cert.id);
        const pdf = await canvasToPdfBlob(canvas);
        zip.file(`${getSafeName(displayCompanyName)}_${cert.id}.pdf`, pdf);
      }
      downloadBlob(
        await zip.generateAsync({ type: "blob" }),
        `${getSafeName(displayCompanyName)}_${selectedBundle}_company_readiness.zip`
      );
    } catch (e) {
      console.error("Error downloading certificate bundle:", e);
      setError("Could not download bundle. Ensure certificates are visible and try again.");
    } finally {
      setBundleLoading(false);
    }
  };

  return (
    <ContentLayout className="items-start rounded-3xl m-2 gap-6 overflow-y-scroll">
      <div className="company-certs-page">
        {/* Hero */}
        <section className="company-certs-hero">
          <div className="company-certs-hero__top">
            <span className="company-certs-hero__badge">
              <MdVerifiedUser className="text-base shrink-0" />
              OnMyWay Readiness Certification
            </span>
            <button
              type="button"
              className="company-certs-hero__refresh"
              onClick={loadCompanyData}
              disabled={loadingCompany}
              title="Refresh organization name from server"
            >
              {loadingCompany ? (
                <VscLoading className="animate-spin text-lg" />
              ) : (
                <MdRefresh className="text-lg" />
              )}
              <span>Refresh</span>
            </button>
          </div>
          <div className="company-certs-hero__main">
            <div className="company-certs-hero__copy">
              <div className="company-certs-hero__org-label">
                <MdBusiness className="text-xl shrink-0" />
                <span>Organization</span>
              </div>
              <h1 className="company-certs-hero__company">
                {loadingCompany && !getCompanyDisplayName(companyData) ? (
                  <span className="inline-flex items-center gap-2 text-white/70">
                    <VscLoading className="animate-spin" /> Loading…
                  </span>
                ) : (
                  displayCompanyName
                )}
              </h1>
              <p className="company-certs-hero__sub">
                Company-level safety readiness certificates for{" "}
                <strong className="text-white">{displayCompanyName}</strong>.
                Documents platform deployment, FARS/DFARS alignment, OSHA/DOL programs,
                and insurance recordkeeping — internal readiness only.
              </p>
            </div>
            <div className="company-certs-hero__actions">
              <CustomButton
                title={
                  bundleLoading ? (
                    <VscLoading className="animate-spin text-xl" />
                  ) : (
                    <span className="flex items-center gap-2">
                      <MdDownload />
                      Bundle ZIP ({bundleCerts.length})
                    </span>
                  )
                }
                onClick={downloadBundleZip}
                disabled={bundleLoading || !!downloadingId}
                className="!py-2.5 !px-5"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <div className="company-certs-stats">
          <div className="company-certs-stat">
            <span className="company-certs-stat__value">
              {COMPANY_CERTIFICATIONS.length}
            </span>
            <span className="company-certs-stat__label">Total readiness certs</span>
          </div>
          <div className="company-certs-stat">
            <span className="company-certs-stat__value">{bundleCerts.length}</span>
            <span className="company-certs-stat__label">
              In {activeBundle?.shortLabel || "bundle"}
            </span>
          </div>
          <div className="company-certs-stat">
            <span className="company-certs-stat__value text-green-400">
              {displayedCerts.length}
            </span>
            <span className="company-certs-stat__label">Showing now</span>
          </div>
          <div className="company-certs-stat">
            <span className="company-certs-stat__value text-amber-400/90 text-lg">
              Active
            </span>
            <span className="company-certs-stat__label">Issued</span>
          </div>
        </div>

        {/* Quick links */}
        <div className="company-certs-quick-links">
          <Link to="/company-safety-policy" className="company-certs-quick-link">
            <MdOutlinePolicy />
            Company Safety Policy
          </Link>
          <Link to="/fars-dfars-compliance" className="company-certs-quick-link">
            <HiOutlineDocumentReport />
            FARS/DFARS 2026 Report
          </Link>
          <Link to="/employee" className="company-certs-quick-link">
            <MdAssessment />
            Employee Certificates
          </Link>
        </div>

        {/* Bundle selector */}
        <section className="company-certs-panel">
          <p className="company-certs-panel__heading">Certification program</p>
          <p className="company-certs-panel__program-name">{companyProgramName}</p>
          <p className="company-certs-panel__description">
            {companyProgramDescription}
          </p>
          <div className="company-certs-bundle-tabs mb-4">
            {CERTIFICATE_BUNDLES.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setSelectedBundle(b.id)}
                className={`company-certs-bundle-tab ${
                  selectedBundle === b.id ? "company-certs-bundle-tab--active" : ""
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
          <label className="company-certs-filter-toggle">
            <input
              type="checkbox"
              checked={showBundleOnly}
              onChange={(e) => setShowBundleOnly(e.target.checked)}
            />
            Show only certificates in selected bundle
          </label>
        </section>

        {showBundleOnly && (
          <div className="company-certs-grid">
            {displayedCerts.map((cert) => renderCompanyCertCard(cert))}
          </div>
        )}

        {/* Certificate grid */}
        {!showBundleOnly && (
        <div className="company-certs-grid">
          {displayedCerts.map((cert) => {
            const inBundle = bundleCerts.some((c) => c.id === cert.id);
            return (
              <article
                key={cert.id}
                className={`company-certs-card ${
                  inBundle ? "company-certs-card--in-bundle" : ""
                }`}
              >
                <div
                  className="company-certs-card__accent"
                  style={{ background: cert.themeColor }}
                />
                <div
                  className="company-certs-card__preview certificate-capture"
                  ref={(el) => {
                    captureRefs.current[cert.id] = el;
                  }}
                >
                  <CertificateCard
                    cert={cert}
                    recipientName={displayCompanyName}
                    showBrandName={false}
                    showIcon={false}
                    elementId={`company-cert-${cert.id}`}
                    className="certificate--modal certificate--square"
                  />
                </div>
                <div className="company-certs-card__body">
                  <div className="company-certs-card__header">
                    <h3 className="company-certs-card__title">{cert.title}</h3>
                    {inBundle ? (
                      <span className="company-certs-card__pill company-certs-card__pill--bundle">
                        In bundle
                      </span>
                    ) : (
                      <span className="company-certs-card__pill">Readiness</span>
                    )}
                  </div>
                  <p className="company-certs-card__desc">{cert.description}</p>
                  <CustomButton
                    title={
                      downloadingId === cert.id ? (
                        <VscLoading className="animate-spin" />
                      ) : (
                        "Download PDF"
                      )
                    }
                    onClick={() => downloadCertPdf(cert.id)}
                    disabled={bundleLoading || downloadingId === cert.id}
                    className="company-certs-card__download !py-2 !text-sm w-full"
                  />
                </div>
              </article>
            );
          })}
        </div>
        )}

        <p className="text-center text-grayLight text-xs pb-4">
          Certificates issued to <strong className="text-primaryText">{displayCompanyName}</strong> ·
          OnMyWay Readiness Certification · Not OSHA, DOL, or FAR/DFARS government credentials
        </p>
      </div>
    </ContentLayout>
  );
};

export default CompanyCertifications;
